import { FloorPlanBounds, LayoutElement } from '../types';
import { isElementVisible } from './layoutImport';
import type { EquipmentInstance } from '../features/equipment/equipmentTypes';
import { polygonBounds } from './equipmentGeometry';

export const SCENE_TARGET_SIZE = 1200;

/** Union of DXF floor bounds and layout element bounds for 3D camera fit. */
export function mergeFloorPlanBounds(
  floor: FloorPlanBounds | null,
  layout: FloorPlanBounds | null
): FloorPlanBounds | null {
  if (!floor && !layout) return null;
  if (!floor) return layout;
  if (!layout) return floor;
  return {
    minX: Math.min(floor.minX, layout.minX),
    minY: Math.min(floor.minY, layout.minY),
    maxX: Math.max(floor.maxX, layout.maxX),
    maxY: Math.max(floor.maxY, layout.maxY),
  };
}

/** Pad / enforce a minimum span so a single small object isn't framed from miles away. */
export function expandBoundsForCamera(
  bounds: FloorPlanBounds,
  minSpan = 120,
  marginFrac = 0.2
): FloorPlanBounds {
  const w = Math.max(bounds.maxX - bounds.minX, 1e-6);
  const h = Math.max(bounds.maxY - bounds.minY, 1e-6);
  const cx = (bounds.minX + bounds.maxX) / 2;
  const cy = (bounds.minY + bounds.maxY) / 2;
  const half = Math.max(w, h, minSpan) * (0.5 + marginFrac);
  return {
    minX: cx - half,
    maxX: cx + half,
    minY: cy - half,
    maxY: cy + half,
  };
}

/** Axis-proportional padding — keeps fab aspect ratio (unlike {@link expandBoundsForCamera}). */
export function padBoundsProportional(
  bounds: FloorPlanBounds,
  marginFrac = 0.06
): FloorPlanBounds {
  const w = Math.max(bounds.maxX - bounds.minX, 1e-6);
  const h = Math.max(bounds.maxY - bounds.minY, 1e-6);
  const padX = w * marginFrac;
  const padY = h * marginFrac;
  return {
    minX: bounds.minX - padX,
    maxX: bounds.maxX + padX,
    minY: bounds.minY - padY,
    maxY: bounds.maxY + padY,
  };
}

/**
 * Bounds the 3D camera should frame: layout elements + equipment overlays.
 * Prefer this over merged floor+layout bounds so a small object isn't stuck in the
 * corner of a huge DXF/floor grid.
 */
export function computeCameraFocusBounds(
  layoutBounds: FloorPlanBounds | null,
  equipmentInstances?: Record<string, EquipmentInstance> | null
): FloorPlanBounds | null {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  const absorb = (b: FloorPlanBounds) => {
    minX = Math.min(minX, b.minX);
    maxX = Math.max(maxX, b.maxX);
    minY = Math.min(minY, b.minY);
    maxY = Math.max(maxY, b.maxY);
  };

  if (layoutBounds) absorb(layoutBounds);

  if (equipmentInstances) {
    for (const inst of Object.values(equipmentInstances)) {
      if (!inst?.polygon || inst.polygon.length < 2) continue;
      const b = polygonBounds(inst.polygon);
      absorb({
        minX: b.minX,
        maxX: b.maxX,
        minY: b.minY,
        maxY: b.maxY,
      });
    }
  }

  if (!Number.isFinite(minX)) return null;
  return expandBoundsForCamera({ minX, minY, maxX, maxY });
}

export type SceneBounds = {
  cx: number;
  cz: number;
  span: number;
  scale: number;
};

export function computeSceneBounds(
  floorBounds: { minX: number; maxX: number; minY: number; maxY: number } | null,
  elements: Record<string, LayoutElement>,
  elementOrder: string[]
): SceneBounds {
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  if (floorBounds) {
    minX = floorBounds.minX;
    maxX = floorBounds.maxX;
    minY = floorBounds.minY;
    maxY = floorBounds.maxY;
  }

  for (const id of elementOrder) {
    const el = elements[id];
    if (!el || !isElementVisible(el)) continue;
    const { x, y } = el.position;
    const w = el.dimensions.width;
    const h = el.dimensions.height;
    minX = Math.min(minX, x, x + w);
    maxX = Math.max(maxX, x, x + w);
    minY = Math.min(minY, y, y + h);
    maxY = Math.max(maxY, y, y + h);
  }

  if (!Number.isFinite(minX)) {
    return { cx: 0, cz: 0, span: 2000, scale: SCENE_TARGET_SIZE / 2000 };
  }

  const cx = (minX + maxX) / 2;
  const cz = (minY + maxY) / 2;
  const span = Math.max(maxX - minX, maxY - minY, 500);
  return { cx, cz, span, scale: SCENE_TARGET_SIZE / span };
}
